import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Car, Search, Star, Upload, Gift, Palmtree, Sparkles, ShieldCheck, X, AlertTriangle } from "lucide-react";
import api, { formatError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/hooks/useFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TierBanner } from "@/components/client/TierBanner";
import { BookingCard } from "@/components/client/BookingCard";

const CLIENT_REASONS = ["model_different", "dirty", "damaged", "mechanical", "equipment_missing", "other"];

export default function MyBookings() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [points, setPoints] = useState(0);
  const [tierInfo, setTierInfo] = useState(null);
  const v3 = useFlags().v3_enabled;
  const [bookings, setBookings] = useState([]);
  const [lookup, setLookup] = useState({ ref: "", email: "" });
  const [reviewFor, setReviewFor] = useState(null);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [disputeFor, setDisputeFor] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [claimFor, setClaimFor] = useState(null);
  const [claimForm, setClaimForm] = useState({ reason: "", details: "", photos: [] });
  const [tab, setTab] = useState("upcoming");
  const today = new Date().toISOString().slice(0, 10);
  const catOf = (b) => b.status === "ongoing" ? "current" : (["completed", "cancelled"].includes(b.status) || b.end_date < today) ? "past" : "upcoming";
  const [docs, setDocs] = useState({});

  const load = () => { if (user) api.get("/my/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {}); };
  useEffect(() => { load(); if (user) { api.get("/me/points").then((r) => { setPoints(r.data.points); setTierInfo(r.data); }).catch(() => {}); api.get("/me/documents").then((r) => setDocs(r.data)).catch(() => {}); } /* eslint-disable-next-line */ }, [user]);

  const uploadDoc = async (kind, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.patch("/me/documents", { [kind]: up.data.url });
      setDocs(data); toast.success(t("docs.saved"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const doLookup = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/bookings/lookup", lookup);
      setBookings([data]);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try {
      const { data } = await api.post("/bookings/cancel", { booking_id: id });
      toast.success(data.free_cancellation ? `${t("common.confirm")} · ${data.refunded} €` : t("common.confirm"));
      if (user) load(); else setBookings((b) => b.map((x) => x.id === id ? { ...x, status: "cancelled" } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.id, rating: review.rating, comment: review.comment, driver_email: reviewFor.driver_email });
      toast.success(t("dash.reviewThanks"));
      setReviewFor(null); setReview({ rating: 5, comment: "" });
      setBookings((b) => b.map((x) => x.id === reviewFor.id ? { ...x, reviewed: true } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-canard">{t("common.loading")}</div>;

  const canReview = (b) => ["confirmed", "ongoing", "completed"].includes(b.status) && !b.reviewed;

  const uploadCondition = async (b, phase, e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const params = new URLSearchParams({ phase }); if (!user) params.set("driver_email", b.driver_email);
    try {
      const { data } = await api.post(`/bookings/${b.id}/photos/upload?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("dash.photoUploaded"));
      setBookings((prev) => prev.map((x) => x.id === b.id ? { ...x, condition_photos: [...(x.condition_photos || []), { by: data.by, phase: data.phase, url: data.url }] } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitDispute = async () => {
    try {
      const body = { reason: disputeReason }; if (!user) body.driver_email = disputeFor.driver_email;
      await api.post(`/bookings/${disputeFor.id}/dispute`, body);
      toast.success(t("dash.disputeSent"));
      setBookings((prev) => prev.map((x) => x.id === disputeFor.id ? { ...x, deposit_dispute: { reason: disputeReason, status: "open" } } : x));
      setDisputeFor(null); setDisputeReason("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openClaim = (b) => { setClaimFor(b); setClaimForm({ reason: "", details: "", photos: [] }); };
  const uploadClaimPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const params = user ? "" : `?driver_email=${encodeURIComponent(claimFor.driver_email)}`;
    try {
      const { data } = await api.post(`/bookings/${claimFor.id}/claim/photo${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClaimForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  const submitClaim = async () => {
    try {
      const body = { reason: claimForm.reason, details: claimForm.details, photos: claimForm.photos };
      if (!user) body.driver_email = claimFor.driver_email;
      const { data } = await api.post(`/bookings/${claimFor.id}/claim`, body);
      toast.success(t("claims.sent"));
      setBookings((prev) => prev.map((x) => x.id === claimFor.id ? { ...x, client_claim: data.claim } : x));
      setClaimFor(null);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const sendClaimMessage = async (b, party, text) => {
    try {
      const body = { text }; if (!user) body.driver_email = b.driver_email;
      const { data } = await api.post(`/claims/${b.id}/${party}/message`, body);
      setBookings((prev) => prev.map((x) => x.id === b.id ? { ...x, [`${party}_claim`]: { ...x[`${party}_claim`], messages: [...(x[`${party}_claim`].messages || []), data.message] } } : x));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="section-sand min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <h1 className="font-heading font-extrabold text-3xl text-ink mb-8">{t("nav.myBookings")}</h1>

        {user && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div data-testid="client-welcome" className="md:col-span-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-canard to-emeraude-dark text-white p-6">
              <Palmtree className="absolute -right-3 -bottom-3 w-28 h-28 text-white/10" />
              <p className="text-xs uppercase font-bold text-white/80 tracking-wide flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {t("cdash.hi", { name: (user.name || "").split(" ")[0] })}</p>
              <h2 className="font-heading font-extrabold text-2xl mt-2">{bookings.length === 0 ? t("cdash.firstTitle") : t("cdash.backTitle")}</h2>
              <p className="text-white/85 font-body text-sm mt-1 max-w-md">{bookings.length === 0 ? t("cdash.firstSub") : t("cdash.backSub")}</p>
              <Button data-testid="client-book-cta" onClick={() => nav("/search")} className="mt-4 rounded-full bg-white text-canard-dark hover:bg-white/90 font-bold"><Car className="w-4 h-4 mr-1" /> {t("cdash.book")}</Button>
            </div>
            <button data-testid="client-points-block" onClick={() => nav("/rewards")} className="text-left rounded-2xl bg-white border border-border p-6 hover:shadow-cta hover:border-canard transition-all">
              <p className="text-xs uppercase font-bold text-muted2 tracking-wide flex items-center gap-1"><Gift className="w-3.5 h-3.5 text-canard" /> {t("plans.points")}</p>
              <p className="font-heading font-extrabold text-4xl text-canard mt-1">{points}</p>
              <p className="text-xs text-muted2 mt-2">{t("cdash.pointsUse")}</p>
              <span className="inline-block mt-3 text-sm font-bold text-canard">{t("cdash.seeRewards")} →</span>
            </button>
          </div>
        )}

        {user && v3 && tierInfo && <TierBanner tierInfo={tierInfo} />}

        {user && (!docs.id_document || !docs.driver_license_doc) && (
          <div data-testid="docs-reminder" className="mb-4 rounded-2xl bg-canard/10 border border-canard/20 p-4 text-sm text-canard-dark font-body flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 shrink-0" /> {t("docs.remind")}
          </div>
        )}

        {user && (
          <div data-testid="client-documents" className="mb-8 rounded-2xl border border-border bg-white p-5">
            <p className="font-heading font-bold text-ink flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-canard" /> {t("docs.title")}</p>
            <p className="text-xs text-muted2 font-body mt-1 mb-4">{t("docs.sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[["id_document", "docId"], ["driver_license_doc", "docLicense"]].map(([k, label]) => (
                <div key={k} className="rounded-xl bg-sand p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">{t(`docs.${label}`)}</p>
                    <p className="text-xs text-muted2">{docs[k] ? t("docs.uploaded") : t("docs.none")}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {docs[k] && <a href={fileUrl(docs[k])} target="_blank" rel="noreferrer" className="text-xs font-bold text-canard">{t("docs.view")}</a>}
                    <label data-testid={`upload-${k}`} className="text-xs font-bold text-white bg-canard hover:bg-canard-dark rounded-full px-3 py-1.5 cursor-pointer flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> {docs[k] ? t("docs.replace") : t("docs.add")}<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(k, e)} /></label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!user && (
          <form onSubmit={doLookup} className="bg-white rounded-2xl shadow-card border border-border p-6 mb-8">
            <p className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><Search className="w-5 h-5 text-canard" /> {t("nav.myBookings")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input data-testid="lookup-ref" placeholder="CD-XXXX" value={lookup.ref} onChange={(e) => setLookup({ ...lookup, ref: e.target.value })} className="h-11 rounded-xl bg-sand" />
              <Input data-testid="lookup-email" type="email" placeholder="Email" value={lookup.email} onChange={(e) => setLookup({ ...lookup, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
              <Button type="submit" data-testid="lookup-submit" className="h-11 rounded-full bg-canard hover:bg-canard-dark">{t("hero.search")}</Button>
            </div>
          </form>
        )}

        {bookings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-border">
            <Car className="w-12 h-12 text-canard/40 mx-auto mb-3" />
            <p className="font-heading font-bold text-ink text-lg">{t("cdash.emptyTitle")}</p>
            <p className="text-muted2 font-body mt-1 mb-5">{t("cdash.emptySub")}</p>
            <Button data-testid="empty-book-cta" onClick={() => nav("/search")} className="rounded-full bg-canard hover:bg-canard-dark font-bold px-6"><Search className="w-4 h-4 mr-1" /> {t("cdash.book")}</Button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-5" data-testid="booking-tabs">
              {["upcoming", "current", "past"].map((k) => {
                const n = bookings.filter((b) => catOf(b) === k).length;
                return (
                  <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                    className={`rounded-full px-4 h-9 text-sm font-bold transition-colors ${tab === k ? "bg-canard text-white" : "bg-white border border-border text-muted2 hover:text-ink"}`}>
                    {t(`cdash.tab_${k}`)} ({n})
                  </button>
                );
              })}
            </div>
          {bookings.filter((b) => catOf(b) === tab).length === 0 ? (
            <p data-testid="tab-empty" className="text-center text-muted2 font-body py-12 bg-white rounded-2xl border border-border">{t(`cdash.tabEmpty_${tab}`)}</p>
          ) : (
          <div className="space-y-4">
            {bookings.filter((b) => catOf(b) === tab).map((b) => (
              <BookingCard key={b.id} b={b} canReview={canReview}
                onReview={setReviewFor} onCancel={cancel} onDispute={setDisputeFor} onUploadCondition={uploadCondition} onClaim={openClaim} onClaimMessage={sendClaimMessage} />
            ))}
          </div>
          )}
          </>
        )}
      </div>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("dash.reviewTitle")}</DialogTitle></DialogHeader>
          {reviewFor && (
            <div className="space-y-4">
              <p className="font-heading font-bold text-ink">{reviewFor.vehicle_name}</p>
              <div>
                <label className="text-sm font-bold text-ink">{t("dash.yourRating")}</label>
                <div className="flex gap-1 mt-2" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setReview({ ...review, rating: n })}>
                      <Star className={`w-8 h-8 transition-colors ${n <= review.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-ink">{t("dash.commentLabel")}</label>
                <Textarea data-testid="review-comment" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} className="mt-1 rounded-xl bg-sand min-h-24" />
              </div>
              <Button data-testid="review-submit" onClick={submitReview} className="w-full h-11 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold">{t("dash.submitReview")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!disputeFor} onOpenChange={(o) => !o && setDisputeFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{t("dash.disputeTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <label className="text-sm font-bold text-ink">{t("dash.disputeReason")}</label>
            <Textarea data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} className="rounded-xl bg-sand min-h-28" />
            <Button data-testid="dispute-submit" disabled={!disputeReason} onClick={submitDispute} className="w-full h-11 rounded-full bg-destructive hover:bg-destructive/90 font-bold">{t("dash.submitDispute")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!claimFor} onOpenChange={(o) => !o && setClaimFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" /> {t("claims.nonConformTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold text-ink">{t("claims.reasonLabel")}</label>
              <select data-testid="claim-reason" value={claimForm.reason} onChange={(e) => setClaimForm({ ...claimForm, reason: e.target.value })}
                className="mt-1 w-full h-11 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
                <option value="">{t("claims.chooseReason")}</option>
                {CLIENT_REASONS.map((r) => <option key={r} value={r}>{t(`claims.r_${r}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-ink">{t("claims.detailsLabel")}</label>
              <Textarea data-testid="claim-details" value={claimForm.details} onChange={(e) => setClaimForm({ ...claimForm, details: e.target.value })} placeholder={t("claims.detailsPh")} className="mt-1 rounded-xl bg-sand min-h-24" />
            </div>
            <div>
              <label className="text-sm font-bold text-ink">{t("claims.photosLabel")}</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {claimForm.photos.map((p, i) => (
                  <div key={p} className="relative w-20 h-16 rounded-lg overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setClaimForm({ ...claimForm, photos: claimForm.photos.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label data-testid="claim-photo-upload" className="w-20 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-canard">
                  <Upload className="w-5 h-5 text-canard" />
                  <input type="file" accept="image/*" onChange={uploadClaimPhoto} className="hidden" />
                </label>
              </div>
            </div>
            <Button data-testid="claim-submit" disabled={!claimForm.reason} onClick={submitClaim} className="w-full h-11 rounded-full bg-destructive hover:bg-destructive/90 font-bold">{t("claims.submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
